/**
 * AdminFounderPriceCard — Super Admin control over the single canonical
 * $79.99 (or whatever) Founder one-time price.
 *
 * Reads/writes through /api/admin/settings (platform_settings.founder_price_cents).
 * Every change requires an audit reason and is written to admin_audit_log.
 * Server-side floor/ceiling ($9.99 – $999.99) prevents accidental typos.
 */
import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, DollarSign, ShieldAlert, Save } from "lucide-react";
import { Button } from "@/components/ui/button";
import { API } from "@/App";

const MIN_CENTS = 999;      // $9.99
const MAX_CENTS = 99_999;   // $999.99

export default function AdminFounderPriceCard() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [currentCents, setCurrentCents] = useState(null);
  const [priceInput, setPriceInput] = useState("");
  const [reason, setReason] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/settings`, { withCredentials: true });
      const cents = Number(r.data.founder_price_cents ?? 7999);
      setCurrentCents(cents);
      setPriceInput((cents / 100).toFixed(2));
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't load Founder price");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const parseCents = (s) => {
    // Strip $, commas, whitespace
    const cleaned = String(s || "").replace(/[$,\s]/g, "");
    if (!/^\d+(\.\d{1,2})?$/.test(cleaned)) return null;
    return Math.round(parseFloat(cleaned) * 100);
  };

  const proposedCents = parseCents(priceInput);
  const hasChange = proposedCents !== null && proposedCents !== currentCents;
  const outOfRange =
    proposedCents !== null && (proposedCents < MIN_CENTS || proposedCents > MAX_CENTS);
  const isBigMove =
    proposedCents !== null && currentCents !== null &&
    Math.abs(proposedCents - currentCents) / currentCents >= 0.25;

  const save = async () => {
    if (proposedCents === null) {
      toast.error("Enter a valid dollar amount");
      return;
    }
    if (outOfRange) {
      toast.error(`Price must be between $${MIN_CENTS / 100} and $${MAX_CENTS / 100}`);
      return;
    }
    if (!reason.trim()) {
      toast.error("Reason is required — this write is audited");
      return;
    }
    if (isBigMove) {
      const pct = Math.round(((proposedCents - currentCents) / currentCents) * 100);
      if (!window.confirm(
        `You're about to change the Founder price by ${pct}%. ` +
        `Existing purchases retain their paid price, but every new Founder pays the new amount. Continue?`,
      )) {
        return;
      }
    }
    setSaving(true);
    try {
      await axios.put(
        `${API}/admin/settings`,
        { founder_price_cents: proposedCents, reason: reason.trim() },
        { withCredentials: true },
      );
      toast.success(`Founder price is now $${(proposedCents / 100).toFixed(2)}`);
      setReason("");
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't update the price");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="rounded-2xl border border-tbc-navy/10 bg-white shadow-sm" data-testid="admin-founder-price-card">
      <div className="p-5 border-b border-tbc-navy/10 flex items-start justify-between gap-3">
        <div>
          <h3 className="font-heading text-lg text-tbc-navy flex items-center gap-2">
            <DollarSign className="w-4 h-4 text-tbc-gold" />
            Founder One-Time Price
          </h3>
          <p className="text-xs text-tbc-navy/60 mt-1">
            Single canonical source for both the displayed price and the Stripe charge — they can never diverge.
          </p>
        </div>
        {currentCents !== null && (
          <div className="text-right">
            <p className="text-[10px] uppercase tracking-widest text-tbc-navy/50">Current</p>
            <p className="font-heading text-3xl text-tbc-gold" data-testid="admin-current-founder-price">
              ${(currentCents / 100).toFixed(2)}
            </p>
          </div>
        )}
      </div>

      <div className="p-5 space-y-3">
        {loading ? (
          <div className="py-6 flex items-center gap-2 text-tbc-navy/50 text-sm">
            <Loader2 className="w-4 h-4 animate-spin" />
            Loading current price…
          </div>
        ) : (
          <>
            <div className="grid sm:grid-cols-[220px_1fr] gap-3">
              <div>
                <label className="text-[10px] uppercase tracking-widest text-tbc-navy/50 block mb-1">New price (USD)</label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-tbc-navy/50">$</span>
                  <input
                    type="text"
                    inputMode="decimal"
                    value={priceInput}
                    onChange={(e) => setPriceInput(e.target.value)}
                    className="w-full pl-6 pr-3 py-2 rounded-lg border border-tbc-navy/15 text-lg font-heading text-tbc-navy focus:outline-none focus:border-tbc-navy"
                    data-testid="admin-founder-price-input"
                  />
                </div>
                {outOfRange && (
                  <p className="text-[11px] text-red-600 mt-1">
                    Out of range — allowed $9.99 to $999.99.
                  </p>
                )}
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-widest text-tbc-navy/50 block mb-1">
                  Audit reason (required when changing)
                </label>
                <textarea
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  placeholder="e.g. Q2 promotional dip to $59.99 through end of month"
                  className="w-full px-3 py-2 rounded-lg border border-tbc-navy/15 text-sm focus:outline-none focus:border-tbc-navy min-h-[70px]"
                  data-testid="admin-founder-price-reason"
                />
              </div>
            </div>

            {isBigMove && (
              <div className="rounded-lg border border-amber-300 bg-amber-50 p-3 flex items-start gap-2 text-xs text-amber-900" data-testid="admin-founder-price-big-move-warn">
                <ShieldAlert className="w-4 h-4 flex-none mt-0.5" />
                <span>
                  This is a large change (≥25%). You&apos;ll be asked to confirm before saving. Historical purchases keep their paid price.
                </span>
              </div>
            )}

            <div className="flex items-center justify-end gap-2">
              <Button
                variant="ghost"
                className="text-tbc-navy/60"
                onClick={() => {
                  if (currentCents !== null) setPriceInput((currentCents / 100).toFixed(2));
                  setReason("");
                }}
                disabled={saving}
              >
                Reset
              </Button>
              <Button
                onClick={save}
                disabled={saving || !hasChange || outOfRange}
                className="bg-tbc-navy text-white hover:bg-tbc-navy/90"
                data-testid="admin-founder-price-save"
              >
                {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : <Save className="w-4 h-4 mr-1.5" />}
                Save new price
              </Button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
